import { ChangeDetectionStrategy, Component, ElementRef, inject, ViewChild } from '@angular/core';
import { BaseModalComponent } from "../../../../../../core/components/modals/base-modal/base-modal.component";
import { SelectData } from "../../../../../../core/types/records-table.types";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatOption } from "@angular/material/core";
import { MatSelect, MatSelectChange } from "@angular/material/select";
import { telegramSaveFileModalFields } from "../../../configs/telegram-save-file-modal-config";
import { MatButton } from "@angular/material/button";
import { FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { SaveFileDialogData } from "../../../types/telegram-save-file-modal.types";
import { TelegramModalsService } from "../../../services/telegram-modals.service";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatInput } from "@angular/material/input";
import { TelegramSaveFileModalService } from "../../../services/telegram-save-file-modal.service";
import { MoveDirection } from "../../../../../../core/types/move-direction.types";
import { telegramSaveFileModalName } from "./configs/telegram-save-file-config";
import { LoaderComponent } from "../../../../../../shared/components/loader/loader.component";

@Component({
  selector: 'app-telegram-save-file-modal',
  standalone: true,
  imports: [
    BaseModalComponent,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    MatButton,
    ReactiveFormsModule,
    MatInput,
    FormsModule,
    LoaderComponent
  ],
  templateUrl: './telegram-save-file-modal.component.html',
  styleUrl: './telegram-save-file-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TelegramSaveFileModalComponent extends BaseModalComponent {
  @ViewChild('matSelect') matSelect!: MatSelect;
  @ViewChild('inputElement') inputElement!: ElementRef<HTMLInputElement>;

  private telegramModalsService: TelegramModalsService = inject(TelegramModalsService);
  private dialogData = inject<SaveFileDialogData>(MAT_DIALOG_DATA);
  private telegramSaveFileModalService: TelegramSaveFileModalService = inject(TelegramSaveFileModalService);

  public readonly telegramSaveFileModalFields = telegramSaveFileModalFields;
  public formGroup: FormGroup = new FormGroup({});
  public searchValue: string = '';
  public selectedValue: string = '';

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    this.modalStatesService.openModal(telegramSaveFileModalName);
    this.formGroup = this.telegramSaveFileModalService.initForm();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(telegramSaveFileModalName);
  }

  public async handleSelectClick(key: string): Promise<void> {
    if (this.getSelectData(key)) {
      this.searchValue = '';
      this.matSelect.open();

      requestAnimationFrame(() => {
        const input = this.inputElement.nativeElement;
        if (input) {
          const length = input.value.length;
          input.setSelectionRange(length, length);
          input.focus();
        }
      });
    }
  }

  public handleSearchTermChange(value: string, key: string): void {
    this.selectedValue = '';
    this.formGroup.get(key)?.setValue('');
    this.searchValue = value;
    this.getSelectData(key);
  }

  public handleKeyEvent(event: KeyboardEvent, fieldName: string): void {
    switch (event.key) {
      case 'ArrowUp':
        this.handleArrowKey(event, 'up', fieldName);
        break;
      case 'ArrowDown':
        this.handleArrowKey(event, 'down', fieldName);
        break;
      case 'Backspace':
        this.handleBackspace(event, fieldName);
        break;
      case 'Enter':
        this.handleEnter(event);
        break;
    }
  }

  public getSelectData(key: string): Array<SelectData> {
    return this.telegramSaveFileModalService.getSelectData(key, this.searchValue);
  }

  public handleSelectionChange(event: MatSelectChange): void {
    const options = this.telegramSaveFileModalService.recordCalenderIdSelectData();
    const selectedOption = options.find(option => option.id === event.value);
    this.selectedValue = selectedOption ? `${selectedOption.id}: ${selectedOption.value}` : '';
    this.searchValue = '';
  }

  public async handleBind(): Promise<void> {
    this.loadingService.setIsLoadingState(true);
    const saveFileParams = this.telegramSaveFileModalService.setSaveFileParams(this.formGroup, this.dialogData.messageId);

    await this.telegramModalsService.saveFile(saveFileParams)
      .then(() => this.snackbarService.showMessage({message: 'Файл успешно привязан'}));

    this.loadingService.setIsLoadingState(false);
    this.closeDialog('cancel');
  }

  private handleEnter(event: Event): void {
    event.preventDefault();
    this.matSelect.close();
    this.inputElement.nativeElement.blur();
  }

  private handleBackspace(event: Event, key: string): void {
    event.preventDefault();
    this.searchValue = '';
    this.selectedValue = '';
    this.formGroup.get(key)?.setValue('');
  }

  private handleArrowKey(event: Event, direction: MoveDirection, key: string): void {
    event.preventDefault();
    const step = direction === 'up' ? -1 : 1;
    this.moveSelection(step, key);
  }

  private moveSelection(step: number, key: string): void {
    const options = this.getSelectData(key);
    if (!options.length) { return; }

    const currentIndex = options.findIndex(option => option.id === this.formGroup.get(key)?.value);
    let newIndex = (currentIndex + step + options.length) % options.length;

    if (currentIndex === -1) {
      newIndex = options.length - 1;
    }

    this.formGroup.get(key)?.setValue(options[newIndex].id);
    this.selectedValue = `${options[newIndex].id}: ${options[newIndex].value}`
    this.telegramSaveFileModalService.scrollSelectedOptionIntoView(newIndex);
  }
}
